import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './EnquiryForm.css';
import { Calendar, User, Mail, Camera, MessageSquare, Send, HelpCircle, CheckCircle2, Target, Zap, Handshake, IndianRupee, Phone } from 'lucide-react';

const EnquiryForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photographers, setPhotographers] = useState([]);
  const [loadingPhotographers, setLoadingPhotographers] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(true);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventType: '',
    customEventType: '',
    eventDate: '',
    eventLocation: '',
    specificRequirements: '',
    preferredPhotographer: '',
    budget: '',
    guestCount: '',
  });

  const [errors, setErrors] = useState({});

  // Event types
  const eventTypes = [
    'Wedding',
    'Pre-Wedding',
    'Engagement',
    'Birthday Party',
    'Corporate Event',
    'Baby Shower',
    'Anniversary',
    'Product Photography',
    'Fashion Shoot',
    'Portfolio Shoot',
    'Other'
  ];

  // ✨ NEW: Load user data and check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await loadUserData(user.uid);
      } else {
        setCurrentUser(null);
        setLoadingUserData(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ✨ NEW: Load user data from Firestore
  const loadUserData = async (userId) => {
    try {
      setLoadingUserData(true);
      
      // Try to fetch from 'users' collection first
      let userDoc = await getDoc(doc(db, 'users', userId));
      let userData = null;
      
      if (userDoc.exists()) {
        userData = userDoc.data();
      } else {
        // If not found in 'users', try 'photographers' collection
        userDoc = await getDoc(doc(db, 'photographers', userId));
        if (userDoc.exists()) {
          userData = userDoc.data();
        }
      }

      if (userData) {
        // ✨ Prefill form with user data
        setFormData(prev => ({
          ...prev,
          name: userData.name || userData.displayName || auth.currentUser?.displayName || '',
          email: userData.email || auth.currentUser?.email || '',
          phone: userData.phone || userData.phoneNumber || '',
        }));
        
        console.log('User data loaded and prefilled:', userData);
        toast.info('Your details have been prefilled!', { autoClose: 2000 });
      } else {
        // If no Firestore data, use auth data
        setFormData(prev => ({
          ...prev,
          name: auth.currentUser?.displayName || '',
          email: auth.currentUser?.email || '',
        }));
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      // Fallback to auth data if Firestore fails
      if (auth.currentUser) {
        setFormData(prev => ({
          ...prev,
          name: auth.currentUser.displayName || '',
          email: auth.currentUser.email || '',
        }));
      }
    } finally {
      setLoadingUserData(false);
    }
  };

  // Load photographers on mount
  useEffect(() => {
    loadPhotographers();
  }, []);

  // Load all available photographers
  const loadPhotographers = async () => {
    try {
      setLoadingPhotographers(true);
      const photographersQuery = query(
        collection(db, 'photographers'),
        where('profileComplete', '==', 100),
        where('portfolioVisible', '==', true)
      );

      const snapshot = await getDocs(photographersQuery);
      const photographersList = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        photographersList.push({
          id: doc.id,
          name: data.name || 'Unknown Photographer',
          specialties: data.specialties || [],
          location: data.location || '',
        });
      });

      setPhotographers(photographersList);
    } catch (error) {
      console.error('Error loading photographers:', error);
    } finally {
      setLoadingPhotographers(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone number must be 10 digits';
    }

    if (!formData.eventType) {
      newErrors.eventType = 'Event type is required';
    }

    if (formData.eventType === 'Other' && !formData.customEventType.trim()) {
      newErrors.customEventType = 'Please specify your event type';
    }

    if (!formData.eventDate) {
      newErrors.eventDate = 'Event date is required';
    }

    if (!formData.eventLocation.trim()) {
      newErrors.eventLocation = 'Event location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper: call serverless API to send mail
  const sendMailViaApi = async (enquiryData) => {
    try {
      const secret = process.env.REACT_APP_MAIL_API_SECRET || 'sdg87sdg987sd9g876sdg'; // set this in Vercel client env if you choose (note: exposing secret in client is insecure)
      const payload = {
        subject: `New Enquiry from ${enquiryData.name}`,
        // plain text fallback
        text: `
New enquiry submitted:

Name: ${enquiryData.name}
Email: ${enquiryData.email}
Phone: ${enquiryData.phone}
Event Type: ${enquiryData.eventType}
Event Date: ${enquiryData.eventDate}
Event Location: ${enquiryData.eventLocation}
Preferred Photographer: ${enquiryData.preferredPhotographer}
Budget: ${enquiryData.budget}
Guest Count: ${enquiryData.guestCount}
Specific Requirements: ${enquiryData.specificRequirements}
        `,
        // HTML version for nicer email
        html: `
          <h2>New Enquiry Submitted</h2>
          <p><strong>Name:</strong> ${enquiryData.name}</p>
          <p><strong>Email:</strong> ${enquiryData.email}</p>
          <p><strong>Phone:</strong> ${enquiryData.phone}</p>
          <p><strong>Event Type:</strong> ${enquiryData.eventType}</p>
          <p><strong>Event Date:</strong> ${enquiryData.eventDate}</p>
          <p><strong>Event Location:</strong> ${enquiryData.eventLocation}</p>
          <p><strong>Preferred Photographer:</strong> ${enquiryData.preferredPhotographer}</p>
          <p><strong>Budget:</strong> ${enquiryData.budget}</p>
          <p><strong>Guest Count:</strong> ${enquiryData.guestCount}</p>
          <p><strong>Specific Requirements:</strong> ${enquiryData.specificRequirements}</p>
          <hr/>
          <p>Source: ${enquiryData.source || 'enquiry_form'}</p>
        `
      };

      const res = await fetch('/api/sendMail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-mail-secret': secret
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (!res.ok) {
        console.error('Mail API error:', data);
        throw new Error(data.error || 'Failed to send mail');
      }
      return data;
    } catch (err) {
      console.error('Error sending mail via API:', err);
      throw err;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare enquiry data
      const enquiryData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        eventType: formData.eventType === 'Other' ? formData.customEventType.trim() : formData.eventType,
        eventDate: formData.eventDate,
        eventLocation: formData.eventLocation.trim(),
        specificRequirements: formData.specificRequirements.trim() || 'None',
        preferredPhotographer: formData.preferredPhotographer || 'No preference',
        budget: formData.budget || 'Not specified',
        guestCount: formData.guestCount || 'Not specified',
        status: 'pending',
        createdAt: new Date(),
        userId: currentUser ? currentUser.uid : null,
        source: 'enquiry_form',
        isRead: false
      };

      // Save to Firestore
      await addDoc(collection(db, 'enquiries'), enquiryData);

      // Attempt to send mail to configured recipient via serverless API.
      // If mail fails, we still proceed (enquiry is saved).
      try {
        await sendMailViaApi(enquiryData);
        toast.success('Enquiry submitted and notification email sent!');
      } catch (mailErr) {
        // If mail fails, notify user but keep enquiry saved
        toast.success('Enquiry submitted successfully! (Email notification failed.)');
      }

      // Reset only event-related fields, keep user details
      setFormData(prev => ({
        ...prev,
        eventType: '',
        customEventType: '',
        eventDate: '',
        eventLocation: '',
        specificRequirements: '',
        preferredPhotographer: '',
        budget: '',
        guestCount: '',
      }));

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // ✨ Show loading state while fetching user data
  if (loadingUserData) {
    return (
      <div className="enquiry-page">
        <div className="enquiry-loading">
          <div className="enquiry-spinner"></div>
          <p>Loading your information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="enquiry-page">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header Section */}
      <div className="enquiry-header">
        <button type="button" className="enquiry-back-btn" onClick={() => navigate(-1)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12" />
            <polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="enquiry-header-content">
          <HelpCircle size={48} className="enquiry-header-icon" />
          <h1 className="enquiry-title">Need Help Planning Your Event?</h1>
          <p className="enquiry-subtitle">
            Confused between many options or need personalized recommendations? 
            We're here to help you find the perfect photographer for your special day!
          </p>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="enquiry-container">
        <div className="enquiry-form-wrapper">
          <div className="enquiry-form-header">
            <h2>Share Your Requirements</h2>
            <p>Fill in the details below and we'll get back to you with the best recommendations</p>
            {currentUser && (
              <div className="enquiry-user-badge">
                <CheckCircle2 size={14} />
                Logged in as {formData.name || currentUser.email}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="enquiry-form">
            {/* Personal Information Section */}
            <div className="enquiry-section">
              <h3 className="enquiry-section-title">
                <User size={20} />
                Personal Information
              </h3>

              <div className="enquiry-form-group">
                <label htmlFor="name" className="enquiry-label">
                  Full Name <span className="enquiry-required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={`enquiry-input ${errors.name ? 'error' : ''}`}
                />
                {errors.name && <span className="enquiry-error-text">{errors.name}</span>}
              </div>

              <div className="enquiry-form-row">
                <div className="enquiry-form-group">
                  <label htmlFor="email" className="enquiry-label">
                    Email Address <span className="enquiry-required">*</span>
                  </label>
                  <div className="enquiry-input-icon">
                    <Mail size={18} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className={`enquiry-input ${errors.email ? 'error' : ''}`}
                    />
                  </div>
                  {errors.email && <span className="enquiry-error-text">{errors.email}</span>}
                </div>

                <div className="enquiry-form-group">
                  <label htmlFor="phone" className="enquiry-label">
                    Phone Number <span className="enquiry-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    className={`enquiry-input ${errors.phone ? 'error' : ''}`}
                    maxLength="10"
                  />
                  {errors.phone && <span className="enquiry-error-text">{errors.phone}</span>}
                </div>
              </div>
            </div>

            {/* Event Details Section */}
            <div className="enquiry-section">
              <h3 className="enquiry-section-title">
                <Camera size={20} />
                Event Details
              </h3>

              <div className="enquiry-form-group">
                <label htmlFor="eventType" className="enquiry-label">
                  Event Type <span className="enquiry-required">*</span>
                </label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleChange}
                  className={`enquiry-select ${errors.eventType ? 'error' : ''}`}
                >
                  <option value="">Select event type</option>
                  {eventTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.eventType && <span className="enquiry-error-text">{errors.eventType}</span>}
              </div>

              {formData.eventType === 'Other' && (
                <div className="enquiry-form-group">
                  <label htmlFor="customEventType" className="enquiry-label">
                    Specify Event Type <span className="enquiry-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="customEventType"
                    name="customEventType"
                    value={formData.customEventType}
                    onChange={handleChange}
                    placeholder="e.g., Family Reunion, Graduation"
                    className={`enquiry-input ${errors.customEventType ? 'error' : ''}`}
                  />
                  {errors.customEventType && <span className="enquiry-error-text">{errors.customEventType}</span>}
                </div>
              )}

              <div className="enquiry-form-row">
                <div className="enquiry-form-group">
                  <label htmlFor="eventDate" className="enquiry-label">
                    Event Date <span className="enquiry-required">*</span>
                  </label>
                  <div className="enquiry-input-icon">
                    <Calendar size={18} />
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      min={getMinDate()}
                      className={`enquiry-input ${errors.eventDate ? 'error' : ''}`}
                    />
                  </div>
                  {errors.eventDate && <span className="enquiry-error-text">{errors.eventDate}</span>}
                </div>

                <div className="enquiry-form-group">
                  <label htmlFor="guestCount" className="enquiry-label">
                    Expected Guest Count
                  </label>
                  <input
                    type="number"
                    id="guestCount"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    placeholder="Approximate number"
                    className="enquiry-input"
                    min="1"
                  />
                </div>
              </div>

              <div className="enquiry-form-group">
                <label htmlFor="eventLocation" className="enquiry-label">
                  Event Location <span className="enquiry-required">*</span>
                </label>
                <input
                  type="text"
                  id="eventLocation"
                  name="eventLocation"
                  value={formData.eventLocation}
                  onChange={handleChange}
                  placeholder="City, Venue name (if known)"
                  className={`enquiry-input ${errors.eventLocation ? 'error' : ''}`}
                />
                {errors.eventLocation && <span className="enquiry-error-text">{errors.eventLocation}</span>}
              </div>

              <div className="enquiry-form-group">
                <label htmlFor="budget" className="enquiry-label">
                  Budget Range (Optional)
                </label>
                <select
                  id="budget"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="enquiry-select"
                >
                  <option value="">Select budget range</option>
                  <option value="Under ₹20,000">Under ₹20,000</option>
                  <option value="₹20,000 - ₹40,000">₹20,000 - ₹40,000</option>
                  <option value="₹40,000 - ₹60,000">₹40,000 - ₹60,000</option>
                  <option value="₹60,000 - ₹1,00,000">₹60,000 - ₹1,00,000</option>
                  <option value="Above ₹1,00,000">Above ₹1,00,000</option>
                  <option value="Flexible">Flexible</option>
                </select>
              </div>
            </div>

            {/* Specific Requirements Section */}
            <div className="enquiry-section">
              <h3 className="enquiry-section-title">
                <MessageSquare size={20} />
                Additional Information
              </h3>

              <div className="enquiry-form-group">
                <label htmlFor="specificRequirements" className="enquiry-label">
                  Specific Requirements
                </label>
                <textarea
                  id="specificRequirements"
                  name="specificRequirements"
                  value={formData.specificRequirements}
                  onChange={handleChange}
                  placeholder="Tell us about any specific requirements, photography style preferences, must-have shots, or any questions you have..."
                  className="enquiry-textarea"
                  rows="5"
                />
                <small className="enquiry-hint">
                  Example: Need candid shots, want drone photography, require same-day edit, etc.
                </small>
              </div>

              <div className="enquiry-form-group">
                <label htmlFor="preferredPhotographer" className="enquiry-label">
                  Preferred Photographer (Optional)
                </label>
                <select
                  id="preferredPhotographer"
                  name="preferredPhotographer"
                  value={formData.preferredPhotographer}
                  onChange={handleChange}
                  className="enquiry-select"
                  disabled={loadingPhotographers}
                >
                  <option value="">No specific preference</option>
                  {photographers.map((photographer) => (
                    <option key={photographer.id} value={photographer.name}>
                      {photographer.name} {photographer.location && `- ${photographer.location}`}
                    </option>
                  ))}
                </select>
                <small className="enquiry-hint">
                  If you found a specific photographer from our portfolio that you liked, let us know! We'll give them first preference.
                </small>
              </div>
            </div>

            {/* Submit Button */}
            <div className="enquiry-form-actions">
              <button
                type="submit"
                className="enquiry-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="enquiry-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Enquiry
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Help Section */}
          <div className="enquiry-help-section">
            <div className="enquiry-help-icon">
              <HelpCircle size={20} />
            </div>
            <h4>What Happens Next?</h4>
            <ul>
              <li>Our team will review your requirements within 24 hours</li>
              <li>We'll shortlist the best photographers matching your needs</li>
              <li>You'll receive personalized recommendations via email</li>
              <li>We'll help you connect with your preferred photographer</li>
            </ul>
          </div>
        </div>

        {/* Sidebar Info */}
        <div className="enquiry-sidebar">
          <div className="enquiry-info-card">
            <h3>Why Use Our Enquiry Service?</h3>
            <div className="enquiry-info-list">
              <div className="enquiry-info-item">
                <span className="enquiry-info-icon"><Target size={17} /></span>
                <div>
                  <h4>Personalized Matching</h4>
                  <p>Get recommendations tailored to your event type and style</p>
                </div>
              </div>
              <div className="enquiry-info-item">
                <span className="enquiry-info-icon"><Zap size={17} /></span>
                <div>
                  <h4>Quick Response</h4>
                  <p>Hear back from us within 24 hours</p>
                </div>
              </div>
              <div className="enquiry-info-item">
                <span className="enquiry-info-icon"><Handshake size={17} /></span>
                <div>
                  <h4>Expert Guidance</h4>
                  <p>Get help from our experienced team</p>
                </div>
              </div>
              <div className="enquiry-info-item">
                <span className="enquiry-info-icon"><IndianRupee size={17} /></span>
                <div>
                  <h4>Best Prices</h4>
                  <p>Connect directly with photographers for competitive rates</p>
                </div>
              </div>
            </div>
          </div>

          <div className="enquiry-info-card enquiry-contact-card">
            <h3>Need Immediate Help?</h3>
            <p>Feel free to reach out to us directly:</p>
            <div className="enquiry-contact-info">
              <a href="tel:+917208353481" className="enquiry-contact-link">
                <Phone size={15} /> +91 7208353481
              </a>
              <a href="mailto:support@litstudio.in" className="enquiry-contact-link">
                <Mail size={15} /> support@litstudio.in
              </a>
            </div>
          </div>
        </div>
      </div>
      
      
    </div>
  );
};

export default EnquiryForm;
